/**
 * HMA Quant Strategies — Central Registry
 *
 * Single source of truth for the metadata of every strategy and benchmark
 * displayed on the Performance Analytics page.
 *
 * To add or modify a series:
 * 1. Update or add the JSON file in /strategies or /benchmarks
 * 2. Import it below
 * 3. Update or add its entry in the strategies[] or benchmarks[] array
 * 4. Also export from /strategies/index.js or /benchmarks/index.js
 *
 * JSON format expected: [{ "date": "YYYY-MM-DD", "value": <number> }, ...]
 *
 * NAME MAPPING NOTE:
 * The underlying JSON files were originally generated with Marot-style names
 * but they hold HMA strategy data:
 * ares.json (was pulse.json — Directional QQQ)
 * ascalafo.json (was helix.json — Directional refined with VIX)
 * bes.json (was vector.json — Multi-layer equity momentum)
 * deimos.json (was vortex.json — Leveraged risk-on/off)
 * fobos.json (was cascade.json — Leveraged risk-on/off with VIX)
 * janus.json (was equipoise.json — Market neutral)
 * tyr.json (was invariant.json — All-Weather diversified)
 * hraesvelgr.json (was aegis.json — All-Weather dual-asset inverse-vol)
 * eos.json (Marot Aurora — intraday MNQ, built by scripts/intraday.py)
 * artemis.json (Marot Salvo — intraday MNQ, one trade per session, scripts/intraday.py)
 * helios.json (Marot Meridian — intraday MNQ, hourly decisions, scripts/intraday.py)
 * hyperion.json (Marot Zenith — intraday MNQ, long only, scripts/intraday.py)
 * horai.json (diversified intraday MNQ programme, scripts/intraday.py)
 * theia.json (intraday MNQ programme, long and short, scripts/intraday.py)
 * aether.json (intraday MNQ programme, scripts/intraday.py)
 * heimdall.json, heimdall_ucits.json (macro-regime allocation, Standard and UCITS; pushed daily by the
 *   publish-web workflow of the Macro-regime-strategy repo)
 * metis.json, metis_ucits.json (multi-asset momentum rotation, Standard and UCITS; scripts/update_metis.py)
 * astraeus.json (community strategy, intraday MNQ, scripts/intraday.py)
 * phaethon.json (community strategy, intraday MNQ, scripts/intraday.py)
 * hemera.json (community strategy, intraday MNQ, scripts/intraday.py)
 * selene.json (community strategy, intraday MNQ, long only, not accepted by the audit, scripts/intraday.py)
 * asteria.json (community strategy, MNQ, long only, Monday session only, scripts/intraday.py)
 */

import {
  tyr,
  hraesvelgr,
  ares,
  janus,
  bes,
  fobos,
  deimos,
  ascalafo,
  eos,
  artemis,
  helios,
  hyperion,
  horai,
  theia,
  astraeus,
  phaethon,
  hemera,
  selene,
  asteria,
  aether,
  heimdall,
  heimdallUcits,
  metis,
  metisUcits,
} from './strategies';

import { spy, qqq, gld, spyGld5050 } from './benchmarks';

// ─────────────────────────────────────────────────────────
// STRATEGIES
// ─────────────────────────────────────────────────────────
export const strategies = [
  {
    id: 'tyr',
    name: 'TYR',
    slug: 'tyr',
    type: 'strategy',
    category: 'All Weather',
    familyId: 'all_weather',
    checkoutKey: 'TYR',
    brochure: '/tyr-brochure.pdf',
    color: '#00FFC8',
    description: 'Diversified All-Weather portfolio with proprietary regime indicators.',
    shortDescription: 'Diversified portfolio across uncorrelated economic regimes.',
    longDescription: 'TYR is a diversified All-Weather inspired portfolio enhanced with proprietary indicators for dynamic asset weighting. Spreads risk across uncorrelated economic regimes (growth, inflation, deflation, recession) and adjusts exposures over time.',
    riskProfile: 'Low-Medium',
    pros: ['Highly uncorrelated to equity markets', 'Performs across multiple economic regimes', 'Lower volatility than equity-only strategies'],
    cons: ['Caps upside during equity bull markets', 'Multiple asset classes increase complexity', 'Returns can feel slow in trending markets'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 1,
    data: tyr,
  },
  {
    id: 'hraesvelgr',
    name: 'HRÆSVELGR',
    slug: 'hraesvelgr',
    type: 'strategy',
    category: 'All Weather',
    familyId: 'all_weather',
    checkoutKey: 'HRAESVELGR',
    color: '#E8B86D',
    description: 'All Weather strategy with a more aggressive profile than TYR.',
    shortDescription: "TYR's more aggressive All Weather cousin, with dynamic risk allocation.",
    longDescription: "HRÆSVELGR is TYR's more aggressive twin: the same All Weather DNA, with a stronger growth tilt. Named for Tyr's father in Norse mythology, it rotates across uncorrelated economic regimes and adjusts risk with proprietary signals, aiming for higher upside than TYR while staying diversified. Built for investors who want All Weather ballast with a more opportunistic stance.",
    riskProfile: 'Medium-High',
    pros: ['All Weather diversification with a stronger growth tilt', 'Adjusts risk dynamically as conditions change', 'Can shift defensive when markets deteriorate', 'Complements TYR rather than duplicating it'],
    cons: ['Higher drawdowns than TYR', 'Not suited to conservative profiles', 'Can lag a pure equity allocation in strong bull markets', 'More opportunistic stance means a bumpier ride'],
    inception_date: '2011-12-02',
    is_public: true,
    order: 2,
    data: hraesvelgr,
  },
  {
    id: 'ares',
    name: 'ARES',
    slug: 'ares',
    type: 'strategy',
    category: 'Directional',
    familyId: 'directional',
    checkoutKey: 'ARES',
    brochure: '/ares-brochure.pdf',
    color: '#EF5350',
    description: 'Directional timing with market health signals.',
    shortDescription: 'Directional strategy on broad indices driven by market health signals.',
    longDescription: 'ARES is a directional strategy that reads structural market health. It rotates between risk-on and risk-off based on a proprietary composite, capturing trends while sidestepping the worst drawdowns.',
    riskProfile: 'Medium',
    pros: ['Captures sustained equity uptrends', 'Active defensive shifts in deteriorating markets', 'Simple to interpret and follow'],
    cons: ['Whipsaws possible in choppy regimes', 'Lags pure buy-and-hold during strong bull markets', 'Concentrated directional exposure'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 3,
    data: ares,
  },
  {
    id: 'janus',
    name: 'JANUS',
    slug: 'janus',
    type: 'strategy',
    category: 'Market Neutral',
    familyId: 'market_neutral',
    checkoutKey: 'JANUS',
    brochure: '/janus-brochure.pdf',
    color: '#A78BFA',
    description: 'Market-neutral long/short equity selection.',
    shortDescription: 'Market-neutral strategy with delta and beta near zero.',
    longDescription: 'JANUS is a market-neutral strategy designed to deliver returns independent of market direction. With delta and beta close to zero, it neutralizes market exposure while extracting alpha through long-short positioning. Low-drawdown return stream that serves as a powerful diversifier in any portfolio.',
    riskProfile: 'Low',
    pros: ['Returns largely uncorrelated to market direction', 'Low drawdowns historically', 'Excellent diversifier for equity-heavy portfolios'],
    cons: ['Lower absolute returns than directional strategies', 'Requires shorting capability', 'Performance can stagnate during strong directional regimes'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 4,
    data: janus,
  },
  {
    id: 'bes',
    name: 'BES',
    slug: 'bes',
    type: 'strategy',
    category: 'Momentum',
    familyId: 'momentum',
    checkoutKey: 'BES',
    brochure: '/bes-brochure.pdf',
    color: '#F59E0B',
    description: 'Multi-layer equity momentum strategy.',
    shortDescription: 'Five-layer momentum strategy on US equities.',
    longDescription: 'BES is a multi-layer momentum strategy applied to US equities. It combines five independent momentum signals spanning short, medium and long horizons to identify the strongest names and rotate the portfolio accordingly.',
    riskProfile: 'Medium-High',
    pros: ['Diversified across multiple momentum horizons', 'Captures both short-term and long-term trends', 'Clear systematic rules'],
    cons: ['Underperforms in mean-reverting markets', 'Higher turnover than directional strategies', 'Concentrated equity exposure'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 5,
    data: bes,
  },
  {
    id: 'fobos',
    name: 'FOBOS',
    slug: 'fobos',
    type: 'strategy',
    category: 'High octane',
    familyId: 'high_octane',
    checkoutKey: 'FOBOS',
    brochure: '/fobos-brochure.pdf',
    color: '#22D3EE',
    description: 'Leveraged risk-on/risk-off strategy with volatility and regression filters.',
    shortDescription: 'Leveraged trend with a volatility-regime filter.',
    longDescription: 'FOBOS is a leveraged risk-on/risk-off strategy. It uses volatility signals and linear regression filters to fine-tune risk management — deploys leverage more conservatively during volatile periods and re-engages faster when conditions stabilize.',
    riskProfile: 'High',
    pros: ['Better drawdown management than pure trend', 'Reacts faster to volatility regime changes', 'High return potential during sustained trends'],
    cons: ['Still leveraged — not low-risk', 'More complex execution', 'Can lag pure trend strategies in calm bull markets'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 6,
    data: fobos,
  },
  {
    id: 'deimos',
    name: 'DEIMOS',
    slug: 'deimos',
    type: 'strategy',
    category: 'High octane',
    familyId: 'high_octane',
    checkoutKey: 'DEIMOS',
    brochure: '/deimos-brochure.pdf',
    color: '#10B981',
    description: 'Leveraged risk-on / risk-off strategy.',
    shortDescription: 'Leveraged trend strategy with proprietary risk filter.',
    longDescription: 'DEIMOS is a leveraged trend-following strategy with a high-octane profile. It uses a proprietary risk-on/risk-off indicator to switch aggressively between exposure and capital preservation. Designed for investors comfortable with higher volatility in exchange for the potential of significantly higher returns during trending markets.',
    riskProfile: 'High',
    pros: ['High return potential during sustained trends', 'Proprietary regime detection', 'Aggressive participation in bull phases'],
    cons: ['Higher drawdowns than unleveraged strategies', 'Whipsaws can be costly with leverage', 'Not suitable for conservative profiles'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 7,
    data: deimos,
  },
  {
    id: 'ascalafo',
    name: 'ASCÁLAFO',
    slug: 'ascalafo',
    type: 'strategy',
    category: 'Directional',
    familyId: 'directional',
    checkoutKey: 'ASCALAFO',
    brochure: '/ascalafo-brochure.pdf',
    color: '#EC4899',
    description: 'Refined directional exposure combining market health and volatility signals.',
    shortDescription: 'Market health plus a volatility trigger with linear regressions for refined entries.',
    longDescription: 'ASCÁLAFO builds on baseline directional models by adding a volatility trigger combined with linear regression signals to refine entries and exits. Improved timing during regime transitions and better positioning around volatility spikes. Smoother performance at the cost of slightly more complex execution.',
    riskProfile: 'Medium',
    pros: ['Improved timing through dual confirmation', 'Better behavior during volatility spikes', 'Smoother equity curve than baseline directional'],
    cons: ['More signals = more turnover', 'Slightly more complex to track', 'Concentrated directional exposure'],
    inception_date: '2006-01-04',
    is_public: true,
    order: 8,
    data: ascalafo,
  },
  {
    id: 'eos',
    name: 'EOS',
    slug: 'eos',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'EOS',
    color: '#FF8A65',
    description: 'Intraday Nasdaq futures, systematic entries in the first hours, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures with confirmed, rules-based entries. Never holds overnight.',
    longDescription: 'EOS is named for the Greek goddess of dawn. It trades Nasdaq futures (MNQ) in the first hours of the session: a systematic, confirmed entry in the direction the day is leaning, a fixed stop and target per trade, and every position closed before the cash close. Audited with YGGDRASIL: Sharpe near 1.5 and near-zero beta to the Nasdaq. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed', 'Shallow drawdowns relative to return at the published size'],
    cons: ['Intraday futures execution — needs a futures account and discipline', 'Edge lives in the modern volatility regime; flat in 2007–2016 per the author', 'Fixed contract sizing: returns scale with the account, not by compounding', 'Backtest curve, not live yet'],
    inception_date: '2019-05-13',
    is_public: true,
    order: 9,
    data: eos,
  },
  {
    id: 'artemis',
    name: 'ARTEMIS',
    slug: 'artemis',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'ARTEMIS',
    color: '#C4B5FD',
    description: 'One systematic trade per session on Nasdaq futures, flat by the close.',
    shortDescription: 'Intraday micro Nasdaq futures. One trade per session, long or short, never overnight.',
    longDescription: 'ARTEMIS is named for the Greek archer goddess: one arrow per session. It takes a single systematic position in Nasdaq futures (MNQ) early in the session, long or short, with a fixed stop and target, and is flat at the close. It stands aside on the main macro days. Audited with YGGDRASIL at one fixed contract: Sharpe 1.3, near-zero beta to the index. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium-High',
    pros: ['Flat every night — no overnight or weekend gap risk', 'One decision per session, early in the day', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Deeper drawdowns than EOS at the same size', 'Audit passed narrowly at fixed size', 'Many trades run to the close: it needs the whole session', 'Backtest curve, not live yet'],
    inception_date: '2019-05-13',
    is_public: true,
    order: 10,
    data: artemis,
  },
  {
    id: 'helios',
    name: 'HELIOS',
    slug: 'helios',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'HELIOS',
    color: '#F4C95D',
    description: 'Intraday trend continuation on Nasdaq futures, decided hour by hour, flat by the close.',
    shortDescription: 'Intraday micro Nasdaq futures. Follows the direction the session is taking, long or short, never overnight.',
    longDescription: 'HELIOS is named for the sun crossing the sky: it follows the path the day is taking. Once an hour it checks whether Nasdaq futures (MNQ) are drifting with conviction in one direction and, if so, joins that move with a fixed stop and target, a few times per session at most, always flat at the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.1, near-zero beta to the index. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium-High',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Trades with the session, not against it', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Weakest audit of the three intraday strategies: passed narrowly at fixed size', 'Lower Sharpe than EOS and ARTEMIS', 'Sensitive to how the early, synthetic years are weighted', 'Backtest curve, not live yet'],
    inception_date: '2019-05-13',
    is_public: true,
    order: 11,
    data: helios,
  },
  {
    id: 'hyperion',
    name: 'HYPERION',
    slug: 'hyperion',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'HYPERION',
    color: '#F97316',
    description: 'Daylight trend on Nasdaq futures, long only, flat by the close.',
    shortDescription: 'Intraday micro Nasdaq futures. Joins the day once it has shown its hand, long only, never overnight.',
    longDescription: 'HYPERION is named for the Titan of light, the one who watches from above. It trades Nasdaq futures (MNQ) intraday and long only: it waits for the day to show its hand, joins it with a fixed stop and target, and is out before the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, near-zero beta to the index. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Long only: no short squeezes', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Small edge per trade: sensitive to costs', '2024 was a losing year', 'The chosen configuration sits in a wide plateau, not on a peak: fine, but the audit flagged its selection', 'Backtest curve, not live yet'],
    inception_date: '2019-06-04',
    is_public: true,
    order: 12,
    data: hyperion,
  },
  {
    id: 'horai',
    name: 'HORAI',
    slug: 'horai',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'HORAI',
    color: '#7DD3FC',
    description: 'Diversified intraday programme on Nasdaq futures, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. Several independent systematic decisions across the session, one curve, never overnight.',
    longDescription: 'HORAI is named for the Greek goddesses of the hours, who share out the day between them. It is a diversified intraday programme on Nasdaq futures (MNQ): the account is spread across independent systematic decisions taken at different points of the session, each with its own rules, all flat at the close. Because those decisions are only weakly correlated, the programme keeps most of the return with a fraction of the drawdown of any single approach. Built from components audited with YGGDRASIL. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Diversification within the session: the smoothest curve of the intraday family', 'Highest Sharpe of the family', 'Flat every night — no overnight or weekend gap risk', 'Near-zero correlation to equity indices'],
    cons: ['Needs an account large enough to hold several whole contracts', 'Several orders per session to execute', 'Only as strong as its weakest component', 'Live on Darwinex only since September 2026: short real track record'],
    inception_date: '2019-05-13',
    is_public: true,
    order: 13,
    data: horai,
  },
  {
    id: 'theia',
    name: 'THEIA',
    slug: 'theia',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'THEIA',
    color: '#F0ABFC',
    description: 'Systematic intraday programme on Nasdaq futures, long and short, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. Several systematic decisions across the session, long and short, one curve, never overnight.',
    longDescription: 'THEIA is named for the Titaness of sight, the one who sees the whole day at once. A systematic intraday programme on Nasdaq futures (MNQ), long and short, with several decisions across the session and nothing held overnight. Audited with YGGDRASIL. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Long and short: no dependence on the market going up', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL'],
    cons: ['Needs an account large enough to hold several whole contracts', 'Several orders per session to execute', 'Sensitive to costs: shown before commissions', 'Backtest curve, not live yet'],
    inception_date: '2019-05-13',
    is_public: true,
    order: 14,
    data: theia,
  },
  {
    id: 'astraeus',
    name: 'ASTRAEUS',
    slug: 'astraeus',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'ASTRAEUS',
    color: '#818CF8',
    description: 'Intraday Nasdaq futures, long and short, one decision a day, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. At most one decision per session, either direction, never overnight.',
    longDescription: 'ASTRAEUS is named for the Titan of dusk, father of the stars and of the four winds. It trades Nasdaq futures (MNQ) intraday, long or short, with at most one decision a day and nothing held into the night. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, zero beta. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Long and short: no dependence on the market going up', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Small edge per trade: sensitive to costs', 'The chosen configuration sits in a wide plateau, not on a peak: fine, but the audit flagged its selection', 'Backtest curve, not live yet', 'Needs whole contracts: account size matters'],
    inception_date: '2019-06-10',
    is_public: true,
    order: 15,
    data: astraeus,
  },
  {
    id: 'phaethon',
    name: 'PHAETHON',
    slug: 'phaethon',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'PHAETHON',
    color: '#FB7185',
    description: 'Intraday Nasdaq futures, long and short, rides the day’s momentum, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. Rides the day’s momentum in either direction with a fixed stop, never overnight.',
    longDescription: 'PHAETHON is named for the son of Helios, who took the chariot of the sun for a day. It trades Nasdaq futures (MNQ) intraday, long or short, riding the day’s momentum with a fixed stop and out before the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, zero beta. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Long and short: no dependence on the market going up', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Sensitive to costs: shown before commissions', 'A few big days carry much of the result', 'Backtest curve, not live yet', 'Needs whole contracts: account size matters'],
    inception_date: '2019-06-10',
    is_public: true,
    order: 16,
    data: phaethon,
  },
  {
    id: 'hemera',
    name: 'HEMERA',
    slug: 'hemera',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'HEMERA',
    color: '#FACC15',
    description: 'Intraday Nasdaq futures, long and short, after the open, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. Enters once the open has shown its hand, either direction, never overnight.',
    longDescription: 'HEMERA is named for the goddess of day, daughter of Night, who brings the light each morning. It trades Nasdaq futures (MNQ) intraday, long or short, once the open has shown its hand, with fixed stop and target and flat at the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.3, zero beta. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Long and short: no dependence on the market going up', 'Near-zero correlation to equity indices', 'Audited with YGGDRASIL: no hard rule failed at fixed size'],
    cons: ['Many trades: the most cost-sensitive of the family', 'Several orders per session to execute', 'Backtest curve, not live yet', 'Needs whole contracts: account size matters'],
    inception_date: '2019-06-10',
    is_public: true,
    order: 17,
    data: hemera,
  },
  {
    id: 'selene',
    name: 'SELENE',
    slug: 'selene',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'SELENE',
    color: '#A5B4FC',
    description: 'Intraday Nasdaq futures, long only, a few days a month, flat every night.',
    shortDescription: 'Intraday micro Nasdaq futures. Long only, a handful of sessions a month, never overnight.',
    longDescription: 'SELENE is named for the Titaness of the moon, sister of Helios and Eos. It trades Nasdaq futures (MNQ) intraday and long only, a few days a month, the ones she chooses, and is flat at the close. Audited with YGGDRASIL at one fixed contract: not accepted, one hard rule failed. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Flat every night — no overnight or weekend gap risk', 'Few trades: easy to follow by hand', 'Near-zero correlation to equity indices', 'Lowest cost drag of the family: few round trips'],
    cons: ['Not accepted by YGGDRASIL: one hard rule failed', 'Few trades: thinner statistics than the rest of the family', 'Long only: sits out falling markets', 'Backtest curve, not live yet'],
    inception_date: '2019-06-10',
    is_public: true,
    order: 18,
    data: selene,
  },
  {
    id: 'asteria',
    name: 'ASTERIA',
    slug: 'asteria',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'ASTERIA',
    color: '#BEF264',
    description: 'Nasdaq futures, long only, once a week: Sunday evening into Monday, flat by Monday’s close.',
    shortDescription: 'Micro Nasdaq futures. Long only, one trade a week on the Monday session, fixed target, no stop.',
    longDescription: 'ASTERIA is named for the Titaness of falling stars, sister of Leto, who became the island of Delos. It trades Nasdaq futures (MNQ) long only, once a week: it buys on Sunday evening for the Monday session and is out before Monday’s close, at a fixed target or on the clock, with no stop. Audited with YGGDRASIL at one fixed contract: accepted, Sharpe 1.3, no hard rule failed; the minimum-sample rule (300 trades) was switched off by the owner because it trades once a week. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['The least correlated with the rest of the family (0.03 with HORAI)', 'Trades only on Monday: complements the other intraday strategies', 'Fixed profit target, and flat by Monday’s close', 'Positive in 7 of the 8 calendar years audited (2026 partial)'],
    cons: ['One trade a week: long stretches flat', 'No stop: a bad Monday is only cut by the clock', 'Long only: sits out falling markets', '2024 was a losing year', 'Backtest curve, not live yet'],
    inception_date: '2019-06-10',
    is_public: true,
    order: 19,
    data: asteria,
  },
  {
    id: 'aether',
    name: 'AETHER',
    slug: 'aether',
    type: 'strategy',
    category: 'Intraday',
    familyId: 'intraday',
    checkoutKey: 'AETHER',
    color: '#5EEAD4',
    description: 'Systematic intraday programme on Nasdaq futures, long and short, flat at every close.',
    shortDescription: 'Intraday micro Nasdaq futures. Several systematic decisions across the session, long and short, one curve, flat at every close.',
    longDescription: 'AETHER is named for the bright upper air the gods breathe, above the weather. A systematic intraday programme on Nasdaq futures (MNQ), long and short, with several decisions across the session and flat at every close. Curve shown at 300% exposure of the account, before commissions.',
    riskProfile: 'Medium',
    pros: ['Long and short: no dependence on the market going up', 'Several decisions spread across the session', 'Flat at every close', 'Shallow drawdowns in the backtest'],
    cons: ['Many orders per session to execute', 'Some positions run from the Sunday evening reopen into Monday without a stop', 'Sensitive to costs: shown before commissions', 'Live on Darwinex only since October 2026: short real track record'],
    inception_date: '2019-06-03',
    is_public: true,
    order: 20,
    data: aether,
  },
  {
    id: 'heimdall',
    name: 'HEIMDALL',
    slug: 'heimdall',
    type: 'strategy',
    category: 'All Weather',
    familyId: 'all_weather',
    checkoutKey: 'HEIMDALL',
    color: '#C9853A',
    description: 'Macro-regime asset allocation: growth and inflation read point in time, with a weekly risk overlay.',
    shortDescription: 'Multi-asset allocation that follows the macro regime, read only from data published at the time. Standard and UCITS versions.',
    longDescription: 'HEIMDALL is named for the watchman of Bifröst, who sees and hears everything and sounds the Gjallarhorn when change comes. Every month it reads US growth and inflation exactly as the data were published at the time (FRED/ALFRED vintages, no later revisions), turns them into the probabilities of four regimes (growth up or down, inflation up or down) and blends the portfolio that suits each one across US equities, Treasuries, TIPS, gold, commodities and T-bills. A weekly risk overlay targets volatility and cuts exposure when stress shows in VIX, MOVE, GVZ or the Baa–10Y credit spread. Two versions share the same signal: Standard, with ETFs, and UCITS, with UCITS-eligible vehicles within UCITS limits.',
    riskProfile: 'Low-Medium',
    pros: ['Follows the macro regime, using only data published at the time', 'Low volatility and shallow drawdowns: max drawdown −8.6% in the backtest, against −20% for a 60/40 at the same volatility target', 'Accepted by YGGDRASIL (Standard version), deflated Sharpe 0.9999 over 71 trials', 'A UCITS version with the same signal'],
    cons: ['A monthly macro signal: between releases only the risk overlay reacts', 'Lags a pure equity allocation in strong bull markets', 'The UCITS version gives up some return to its concentration limits', 'Backtest curve, not live yet'],
    inception_date: '2007-09-04',
    is_public: true,
    order: 21,
    data: heimdall,
    // Same signal, two implementations. The page shows a Standard | UCITS switch (StrategyVariantContext).
    variants: { standard: heimdall, ucits: heimdallUcits },
    defaultVariant: 'standard',
  },
  {
    id: 'metis',
    name: 'METIS',
    slug: 'metis',
    type: 'strategy',
    category: 'All Weather',
    familyId: 'all_weather',
    checkoutKey: 'METIS',
    color: '#A3B86C',
    description: 'Multi-asset momentum rotation: each month, the three strongest of eight markets, or cash.',
    shortDescription: 'Monthly rotation across equities, bonds, gold, commodities and real estate, with a volatility brake. Standard and UCITS versions.',
    longDescription: 'METIS is named for the Titaness of wise counsel, first wife of Zeus and mother of Athena. On the last trading day of each month it looks at eight markets (US, developed and emerging equities, long and intermediate Treasuries, gold, commodities and US real estate) and holds the three that have risen most over six months, in equal parts and only if they are up; whatever is left goes to T-bills. A volatility brake scales the portfolio down when its volatility runs above 15%. It never uses leverage. Two versions share the same signal: Standard, with US ETFs, and UCITS, with gold and commodities capped at 20% each.',
    riskProfile: 'Medium',
    pros: ['About the return of the S&P 500 with less than half its worst fall: max drawdown −20% against −55% in the backtest (2007–2026)', 'Gained 17.8% in 2008, while the S&P 500 lost 36.8%', 'Accepted by YGGDRASIL (Standard version), deflated Sharpe 0.995 over 50 trials', 'A UCITS version with the same signal'],
    cons: ['Runs of bad years: −12.7% in 2015 and −5.1% in 2018', 'Has lagged the S&P 500 in most years since 2019', 'The UCITS version was not accepted by YGGDRASIL (two hard rules) and gives up 1.4 points a year to its caps', 'Backtest curve, not live yet'],
    inception_date: '2007-01-03',
    is_public: true,
    order: 22,
    data: metis,
    // Same signal, two implementations. The page shows a Standard | UCITS switch (StrategyVariantContext).
    variants: { standard: metis, ucits: metisUcits },
    defaultVariant: 'standard',
  },
];

// ─────────────────────────────────────────────────────────
// BENCHMARKS
// ─────────────────────────────────────────────────────────
export const benchmarks = [
  {
    id: 'spy',
    name: 'S&P 500',
    slug: 'spy',
    type: 'benchmark',
    category: 'index',
    color: '#9CA3AF',
    description: 'S&P 500 index (SPY ETF).',
    inception_date: '2006-01-04',
    is_public: true,
    order: 1,
    data: spy,
  },
  {
    id: 'qqq',
    name: 'Nasdaq 100',
    slug: 'qqq',
    type: 'benchmark',
    category: 'index',
    color: '#60A5FA',
    description: 'Nasdaq 100 index (QQQ ETF).',
    inception_date: '2006-01-04',
    is_public: true,
    order: 2,
    data: qqq,
  },
  {
    id: 'gld',
    name: 'Gold',
    slug: 'gld',
    type: 'benchmark',
    category: 'commodity',
    color: '#EAB308',
    description: 'Gold (GLD ETF).',
    inception_date: '2006-01-04',
    is_public: true,
    order: 3,
    data: gld,
  },
  {
    id: 'spy-gld-50-50',
    name: 'Equity & Gold 50/50',
    slug: 'spy-gld-50-50',
    type: 'benchmark',
    category: 'allocation',
    color: '#A78BFA',
    description: '50/50 split between S&P 500 and gold.',
    inception_date: '2006-01-04',
    is_public: true,
    order: 4,
    data: spyGld5050,
  },
];

// ─────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────
export const registry = [...strategies, ...benchmarks];

export const getById = (id) => registry.find((item) => item.id === id);
export const getBySlug = (slug) => {
  const key = String(slug || '').toLowerCase();
  return strategies.find((item) => item.slug === key || item.id === key);
};
export const getStrategies = () => strategies;
export const getBenchmarks = () => benchmarks;
export const getStrategiesByFamily = (familyId) =>
  strategies.filter((item) => item.familyId === familyId);
